import React, { useState, useCallback, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';

/* ═══════════════════════════════════════════════════
   TYPES
═══════════════════════════════════════════════════ */
interface Feature {
  id: string;
  name: string;
  status: 'live' | 'partial' | 'mock' | 'wip' | 'todo';
  desc: string;
  adminRequired: boolean;
  adminNote?: string;
  deps: string[];
}

interface Category {
  id: string;
  icon: string;
  label: string;
  subtitle: string;
  color: string;
  features: Feature[];
}

type FilterType = 'all' | 'live' | 'partial' | 'mock';
type ModalTab = 'spec' | 'setup';

/* ═══════════════════════════════════════════════════
   FEATURE DATA
═══════════════════════════════════════════════════ */
const CATEGORIES: Category[] = [
  {
    id: 'core-banking', icon: '🏦', label: 'Core Banking',
    subtitle: 'Essential account & payment features', color: '#dbeafe',
    features: [
      { id: 'dashboard',      name: 'Dashboard',              status: 'live',    desc: 'Account balance overview, recent transactions, quick actions, time-aware greeting, WebSocket notifications.',  adminRequired: false, deps: [] },
      { id: 'transactions',   name: 'Transaction History',    status: 'live',    desc: 'Full paginated transaction list with search, date/amount/type filters, and CSV/PDF export.',                 adminRequired: false, deps: [] },
      { id: 'account',        name: 'Account Details',        status: 'live',    desc: 'IBAN/BIC display, balance chart (7/30/90d), statement PDF download, linked external accounts.',              adminRequired: false, deps: [] },
      { id: 'sepa-instant',   name: 'SEPA Instant Transfer',  status: 'live',    desc: 'Send money instantly to any IBAN. Fee: €1.00. Real-time arrival. Maximum transfer limits apply.',            adminRequired: false, deps: [] },
      { id: 'sepa-standard',  name: 'SEPA Standard Transfer', status: 'live',    desc: 'Standard SEPA bank transfer, lower fee, 1–2 business day settlement.',                                       adminRequired: false, deps: [] },
      { id: 'payees',         name: 'Payees / Address Book',  status: 'live',    desc: 'Save, edit, and delete payment recipients. One-click pay to saved payees.',                                  adminRequired: false, deps: [] },
      { id: 'batch-payments', name: 'Batch Payments',         status: 'live',    desc: 'CSV upload of multiple payments, row-level validation, bulk execution with status tracking per payment.',    adminRequired: false, deps: [] },
      { id: 'cards',          name: 'Card Management',        status: 'live',    desc: 'Freeze/unfreeze, view PIN, spending controls, per-card transaction history, order new card.',                adminRequired: false, deps: [] },
      { id: 'savings',        name: 'Savings Goals',          status: 'live',    desc: 'Named goals with target amounts/dates, manual top-up, auto-save rules, progress tracking.',                  adminRequired: false, deps: [] },
      { id: 'notifications',  name: 'Notifications Centre',   status: 'live',    desc: 'Real-time WebSocket notifications, mark-as-read, preference configuration, unread badge count.',            adminRequired: false, deps: [] },
      { id: 'profile',        name: 'Profile & Settings',     status: 'live',    desc: 'Edit personal details, profile photo, change password, 2FA (TOTP), language/region, session settings.',     adminRequired: false, deps: [] },
      { id: 'child-wallets',  name: 'Child Wallets',          status: 'live',    desc: 'Parent-controlled sub-wallets with spending limits, merchant category blocks, and recurring allowances.',    adminRequired: false, deps: [] },
    ]
  },
  {
    id: 'crypto', icon: '₿', label: 'Crypto & Blockchain',
    subtitle: 'Multi-chain wallets, stablecoins & on-chain tools', color: '#fed7aa',
    features: [
      { id: 'crypto-wallet', name: 'Multi-Chain Crypto Wallet', status: 'partial', desc: 'ETH, BTC, SOL, MATIC, XLM, ADA wallets. Live price charts, send/receive, swap, on-chain tx history. Note: blockchain.enabled=false — addresses/balances are currently mocked.', adminRequired: false, deps: [] },
      { id: 'stablecoin',    name: 'Stablecoin Payments',       status: 'mock',    desc: 'USDC/USDT send, receive, and fiat-to-stablecoin convert across Ethereum, Solana, Polygon. Backend endpoints exist; web UI not yet wired. See STABLECOIN_IMPLEMENTATION.md.', adminRequired: false, deps: ['crypto-wallet'] },
      { id: 'multisig',      name: 'Multi-Signature Wallets',   status: 'partial', desc: 'Create M-of-N wallets, manage signatories, propose and approve on-chain transactions. On-chain execution mocked (blockchain disabled).', adminRequired: false, deps: [] },
      { id: 'kyt-retail',    name: 'Address Risk Check (KYT)',  status: 'live',    desc: 'KYT risk scoring on any blockchain address before sending — sanctions screening, darknet detection, entity labels.', adminRequired: false, deps: ['crypto-wallet'] },
    ]
  },
  {
    id: 'trading', icon: '📈', label: 'Trading & Investments',
    subtitle: 'Automated bots, MT5, order book & tokenized assets', color: '#ede9fe',
    features: [
      { id: 'trading-bots',     name: 'Retail Trading Bots',    status: 'live',    desc: 'Eligibility assessment, risk consent, capital allocation to admin-created strategies, live P&L tracking, trade history drill-down.', adminRequired: true, adminNote: 'Admin must create & activate trading strategies first', deps: [] },
      { id: 'profit-analytics', name: 'Profit Analytics',       status: 'live',    desc: 'Per-strategy P&L charts, trade-level detail, realised vs unrealised breakdown, PDF report export.', adminRequired: false, deps: ['trading-bots'] },
      { id: 'mt5-trading',      name: 'MT5 Forex/CFD Trading',  status: 'partial', desc: 'Connect MT5 account, live charts (M1–W1), order placement (market/limit), open positions list, close position.', adminRequired: true, adminNote: 'MT5 provider must be configured in Admin → Provider Config', deps: [] },
      { id: 'order-book',       name: 'Order Book (Exchange)',   status: 'live',    desc: 'Internal exchange order book: live bids/asks, place market/limit orders, open orders management, trade history.', adminRequired: true, adminNote: 'Admin must configure trading pairs in Matching Engine', deps: [] },
      { id: 'assets',           name: 'Tokenized Assets',       status: 'partial', desc: 'Stocks, real estate, bonds, commodities, stablecoins as purchasable digital tokens. Portfolio value, buy/sell, transaction history.', adminRequired: true, adminNote: 'Admin must create asset tokens in Asset Tokens module', deps: [] },
    ]
  },
  {
    id: 'gaming', icon: '🎮', label: 'Gaming',
    subtitle: 'iGaming, P2P skill games & social features', color: '#fce7f3',
    features: [
      { id: 'igaming',      name: 'iGaming (Casino & Sports)', status: 'live', desc: 'Casino slots/tables/live dealer, sports betting. Separate iGaming account balance, deposit from main account.', adminRequired: true, adminNote: 'Admin enables iGaming per customer and manages game catalogue', deps: [] },
      { id: 'p2p-games',    name: 'P2P Skill Games',           status: 'live', desc: 'Create/accept challenges, stake real money, real-time quiz game lobby, match history, auto stake transfer to winner.', adminRequired: false, deps: ['friends'] },
      { id: 'friends',      name: 'Friends & Social',          status: 'live', desc: 'Search users, send/accept friend requests, friends list with online status, quick-pay and challenge shortcuts.', adminRequired: false, deps: [] },
      { id: 'social-feed',  name: 'Social Media Feed',         status: 'live', desc: 'Platform content feed (announcements, tips, promos), like/comment engagement. Admin publishes content.', adminRequired: true, adminNote: 'Admin creates and manages all content via Social Media module', deps: [] },
    ]
  },
  {
    id: 'admin-core', icon: '🛡️', label: 'Admin Panel — Core',
    subtitle: 'Customer operations, compliance & reporting', color: '#ede9fe',
    features: [
      { id: 'admin-dashboard',  name: 'Admin Dashboard Stats',     status: 'live', desc: 'KPI cards: customers, transaction volume, compliance alerts, active bots, AUM. KuCoin live balances.', adminRequired: true, deps: [] },
      { id: 'admin-customers',  name: 'Customer Management',       status: 'live', desc: 'Customer list, full profile modal, KYC/KYB review & approve/reject, account activate/suspend, iGaming control, trading summary, GDPR export.', adminRequired: true, deps: [] },
      { id: 'admin-accounts',   name: 'Account Management',        status: 'live', desc: 'Platform-wide account list, freeze/unfreeze, manual balance adjustment (credits/debits), statement download.', adminRequired: true, deps: [] },
      { id: 'admin-txns',       name: 'Transaction Management',    status: 'live', desc: 'Full platform transaction list with advanced filters, transaction detail + flow diagram, reversal capability.', adminRequired: true, deps: [] },
      { id: 'admin-compliance', name: 'Compliance & AML Alerts',   status: 'live', desc: 'AML alert queue (HIGH/MEDIUM/LOW severity), resolve/escalate workflow, SAR generation, review queue for KYC/payments.', adminRequired: true, deps: [] },
      { id: 'admin-reports',    name: 'Reports & Analytics',       status: 'live', desc: 'Strategy performance reports, revenue breakdowns, user acquisition analytics, regulatory exports, PDF with embedded charts.', adminRequired: true, deps: [] },
      { id: 'admin-audit',      name: 'Audit Logs',                status: 'live', desc: 'Immutable log of all admin actions with before/after values. Search by user, action type, date. CSV export.', adminRequired: true, deps: [] },
      { id: 'admin-users',      name: 'Admin Users & Permissions', status: 'live', desc: 'Create admin accounts, assign granular permissions (26 permission flags), suspend/reactivate admins, role management.', adminRequired: true, deps: [] },
      { id: 'admin-settings',   name: 'Admin Settings',            status: 'live', desc: 'Banking limits/fees, security policies (2FA, timeout), JWT session config, notification/SMTP settings, feature flags.', adminRequired: true, deps: [] },
    ]
  },
  {
    id: 'admin-trading', icon: '🤖', label: 'Admin Panel — Trading',
    subtitle: 'Strategy creation, bots, provider & asset management', color: '#ede9fe',
    features: [
      { id: 'admin-trading-bots',    name: 'Trading Strategy Management',  status: 'live',    desc: 'Create/edit/clone strategies (Basic + Enhanced V2), start/pause/stop, coin selection, AI coin prediction, meme blacklist, live positions & manual close, strategy reports.', adminRequired: true, deps: ['trading-bots'] },
      { id: 'admin-kucoin',          name: 'KuCoin Account Management',    status: 'live',    desc: 'Add KuCoin API credentials, view live balances (USDT/BTC/tokens), funding history, assign accounts to strategies.', adminRequired: true, deps: ['admin-trading-bots'] },
      { id: 'admin-provider',        name: 'Trading Provider Config',      status: 'live',    desc: 'Switch between KuCoin, MT5, OANDA, or Mock providers. Sync credentials from DB to running service.', adminRequired: true, deps: [] },
      { id: 'admin-asset-tokens',    name: 'Asset Token Management',       status: 'partial', desc: 'Create/edit tokenized assets (STABLECOIN, SECURITY, COMMODITY, UTILITY), set prices & supply, activate/suspend tokens for retail.', adminRequired: true, deps: ['assets'] },
      { id: 'admin-stablecoin-mgmt', name: 'Stablecoin Payment Management',status: 'todo',    desc: 'Dedicated admin control panel to enable/disable specific stablecoins (USDC, USDT, DAI, EURX) and payment networks (Ethereum, Solana, Polygon) available to retail users. Per-coin transaction limits, per-user/tier access control, global stablecoin feature on/off switch.', adminRequired: true, adminNote: 'To be built — retail stablecoin availability is currently hardcoded in the UI.', deps: ['stablecoin', 'admin-asset-tokens'] },
      { id: 'admin-order-book',      name: 'Order Book & Matching Engine', status: 'live',    desc: 'Platform-wide order view, cancel any order, configure trading pairs (min/max size, tick), matching engine status & algorithm config.', adminRequired: true, deps: ['order-book'] },
      { id: 'admin-eligibility',     name: 'Eligibility Management',       status: 'live',    desc: 'Define trading eligibility criteria (KYC, balance, jurisdiction), view eligible users, manual override with reason.', adminRequired: true, deps: ['trading-bots'] },
    ]
  },
  {
    id: 'admin-ops', icon: '⚙️', label: 'Admin Panel — Operations',
    subtitle: 'KYT, risk, health, gaming & content management', color: '#cffafe',
    features: [
      { id: 'admin-kyt',            name: 'KYT Analysis',               status: 'live',    desc: 'Blockchain trace for any address/tx hash, hop-by-hop fund flow, risk scoring, entity resolution (Binance/Tornado Cash etc.), KYT report PDF.', adminRequired: true, deps: [] },
      { id: 'admin-risk',           name: 'Risk Management',            status: 'live',    desc: 'Platform risk exposure dashboard, VaR, drawdown metrics, risk alerts, position reconciliation against exchange.', adminRequired: true, deps: [] },
      { id: 'admin-health',         name: 'System Health',              status: 'live',    desc: 'Live service status for all platform components, API performance metrics, scheduled job monitoring (@Scheduled tasks).', adminRequired: true, deps: [] },
      { id: 'admin-igaming',        name: 'iGaming Management',         status: 'live',    desc: 'Game catalogue management, per-player analytics & GGR, responsible gambling limits, bonus management.', adminRequired: true, deps: ['igaming'] },
      { id: 'admin-p2p',            name: 'P2P Games Management',       status: 'live',    desc: 'All challenge oversight, dispute resolution & outcome reversal, question bank management, platform fee configuration.', adminRequired: true, deps: ['p2p-games'] },
      { id: 'admin-social',         name: 'Social Media Content Mgmt',  status: 'live',    desc: 'Create/edit/delete platform posts and announcements, rich text editor, engagement analytics (views/likes/comments).', adminRequired: true, deps: ['social-feed'] },
      { id: 'admin-crypto-payouts', name: 'Crypto Payouts',             status: 'partial', desc: 'Manual crypto payout initiation, four-eyes approval for large amounts, payout queue with on-chain status. Note: on-chain sends mocked (blockchain disabled).', adminRequired: true, deps: [] },
      { id: 'admin-provider-config',name: 'Provider Configuration',     status: 'live',    desc: 'Manage integration credentials: TrueLayer (open banking), Sumsub (KYC), Transak (on-ramp), blockchain RPC endpoints.', adminRequired: true, deps: [] },
      { id: 'admin-products',       name: 'Products Management',        status: 'live',    desc: 'Define account types, card tiers, subscription plans. Activate/deactivate products and update pricing.', adminRequired: true, deps: [] },
      { id: 'admin-review-queue',   name: 'Review Queue',               status: 'live',    desc: 'Unified queue for pending KYC reviews, large payment approvals, account change requests. Quick approve/reject actions.', adminRequired: true, deps: ['admin-compliance'] },
      { id: 'admin-multisig',       name: 'Multi-Sig Wallet Management',status: 'partial', desc: 'Admin-side view of all multi-sig wallets across the platform. Monitor pending proposals and approval counts.', adminRequired: true, deps: ['multisig'] },
    ]
  }
];

/* ═══════════════════════════════════════════════════
   PRESETS
═══════════════════════════════════════════════════ */
const ALL_FEATURES = CATEGORIES.flatMap(c => c.features);
const PRESETS: { [key: string]: string[] } = {
  basic:   ['dashboard','transactions','account','sepa-instant','sepa-standard','payees','batch-payments','cards','savings','notifications','profile','admin-dashboard','admin-customers','admin-accounts','admin-txns','admin-settings'],
  trading: ['dashboard','transactions','account','sepa-instant','cards','profile','notifications','trading-bots','profit-analytics','mt5-trading','order-book','assets','admin-dashboard','admin-customers','admin-trading-bots','admin-kucoin','admin-provider','admin-asset-tokens','admin-order-book','admin-eligibility','admin-reports'],
  crypto:  ['dashboard','transactions','account','sepa-instant','cards','profile','notifications','crypto-wallet','stablecoin','multisig','kyt-retail','admin-dashboard','admin-customers','admin-kyt','admin-crypto-payouts','admin-provider-config'],
  gaming:  ['dashboard','transactions','account','sepa-instant','cards','profile','notifications','igaming','p2p-games','friends','social-feed','admin-dashboard','admin-customers','admin-igaming','admin-p2p','admin-social'],
  full:    CATEGORIES.flatMap(c => c.features.map(f => f.id)),
};

/* ═══════════════════════════════════════════════════
   PERMISSION MAP
═══════════════════════════════════════════════════ */
const FEATURE_PERMISSION_MAP: { [key: string]: { admin: string[]; retail: string[] } } = {
  'dashboard':             { admin:['VIEW_ADMIN_DASHBOARD'], retail:['VIEW_RETAIL_DASHBOARD','VIEW_RETAIL_DASHBOARD_BALANCE','VIEW_RETAIL_DASHBOARD_QUICK_ACTIONS','VIEW_RETAIL_DASHBOARD_ACCOUNTS','VIEW_RETAIL_DASHBOARD_TRANSACTIONS'] },
  'transactions':          { admin:['VIEW_TRANSACTIONS','VIEW_ADMIN_TRANSACTIONS'], retail:['VIEW_RETAIL_TRANSACTIONS'] },
  'account':               { admin:['VIEW_ACCOUNTS','EDIT_ACCOUNTS','VIEW_ADMIN_ACCOUNTS'], retail:['VIEW_RETAIL_ACCOUNTS'] },
  'sepa-instant':          { admin:['VIEW_TRANSACTIONS','APPROVE_TRANSACTIONS','VIEW_ADMIN_TRANSACTIONS'], retail:['VIEW_RETAIL_TRANSFER','VIEW_RETAIL_PAYMENTS'] },
  'sepa-standard':         { admin:['VIEW_TRANSACTIONS','APPROVE_TRANSACTIONS','VIEW_ADMIN_TRANSACTIONS'], retail:['VIEW_RETAIL_TRANSFER','VIEW_RETAIL_PAYMENTS'] },
  'payees':                { admin:['VIEW_TRANSACTIONS','VIEW_ADMIN_TRANSACTIONS'], retail:['VIEW_RETAIL_PAYMENTS'] },
  'batch-payments':        { admin:['VIEW_TRANSACTIONS','APPROVE_TRANSACTIONS','VIEW_ADMIN_TRANSACTIONS'], retail:['VIEW_RETAIL_PAYMENTS'] },
  'cards':                 { admin:['VIEW_ACCOUNTS','VIEW_ADMIN_ACCOUNTS'], retail:['VIEW_RETAIL_CARDS'] },
  'savings':               { admin:['VIEW_ACCOUNTS','VIEW_ADMIN_ACCOUNTS'], retail:['VIEW_RETAIL_SAVINGS'] },
  'notifications':         { admin:['VIEW_ADMIN_NOTIFICATIONS'], retail:['VIEW_RETAIL_NOTIFICATIONS'] },
  'profile':               { admin:['VIEW_CUSTOMERS','VIEW_ADMIN_CUSTOMERS'], retail:['VIEW_RETAIL_PROFILE','VIEW_RETAIL_SETTINGS'] },
  'child-wallets':         { admin:['VIEW_CUSTOMERS','VIEW_ADMIN_CUSTOMERS'], retail:['VIEW_RETAIL_CHILD_WALLETS'] },
  'crypto-wallet':         { admin:['VIEW_KYT','VIEW_ADMIN_KYT'], retail:['VIEW_RETAIL_CRYPTO'] },
  'stablecoin':            { admin:['VIEW_ADMIN_STABLECOIN','MANAGE_STABLECOIN_CONFIG'], retail:['VIEW_RETAIL_CRYPTO'] },
  'multisig':              { admin:['VIEW_CUSTOMERS','VIEW_ADMIN_CUSTOMERS'], retail:['VIEW_RETAIL_CRYPTO'] },
  'kyt-retail':            { admin:['VIEW_KYT','VIEW_ADMIN_KYT'], retail:['VIEW_RETAIL_CRYPTO'] },
  'trading-bots':          { admin:['MANAGE_TRADING_BOTS','VIEW_TRADING_BOT_ANALYTICS','VIEW_ADMIN_TRADING_BOTS'], retail:['VIEW_RETAIL_TRADING_BOTS'] },
  'profit-analytics':      { admin:['VIEW_TRADING_BOT_ANALYTICS','VIEW_ADMIN_TRADING_BOTS'], retail:['VIEW_RETAIL_TRADING_BOTS'] },
  'mt5-trading':           { admin:['MANAGE_PROVIDER_CONFIG','VIEW_ADMIN_SETTINGS'], retail:['VIEW_RETAIL_MT5_TRADING'] },
  'order-book':            { admin:['MANAGE_ORDER_BOOK_PROVIDERS','VIEW_ORDER_BOOK_ANALYTICS','VIEW_ADMIN_ORDER_BOOK'], retail:['VIEW_RETAIL_ORDER_BOOK','SUBMIT_TRADING_ORDERS','CANCEL_TRADING_ORDERS','VIEW_TRADING_HISTORY'] },
  'assets':                { admin:['VIEW_ASSET_TOKENS','MANAGE_ASSET_TOKENS','VIEW_ADMIN_ASSET_TOKENS'], retail:['VIEW_RETAIL_ASSETS'] },
  'igaming':               { admin:['VIEW_ADMIN_IGAMING'], retail:['VIEW_RETAIL_IGAMING'] },
  'p2p-games':             { admin:['VIEW_ADMIN_P2PGAME'], retail:['VIEW_RETAIL_P2PGAME'] },
  'friends':               { admin:['VIEW_CUSTOMERS','VIEW_ADMIN_CUSTOMERS'], retail:['VIEW_RETAIL_SOCIAL_MEDIA'] },
  'social-feed':           { admin:['MANAGE_SOCIAL_MEDIA_POSTS','VIEW_SOCIAL_MEDIA_ANALYTICS','VIEW_ADMIN_SOCIAL_MEDIA'], retail:['VIEW_RETAIL_SOCIAL_MEDIA'] },
  'admin-dashboard':       { admin:['VIEW_ADMIN_DASHBOARD','VIEW_DASHBOARD_TOTAL_CUSTOMERS','VIEW_DASHBOARD_ACTIVE_CUSTOMERS','VIEW_DASHBOARD_TOTAL_ACCOUNTS','VIEW_DASHBOARD_PENDING_TRANSACTIONS','VIEW_DASHBOARD_SYSTEM_HEALTH','VIEW_DASHBOARD_QUICK_INSIGHTS','VIEW_DASHBOARD_SYSTEM_STATUS'], retail:[] },
  'admin-customers':       { admin:['VIEW_CUSTOMERS','EDIT_CUSTOMERS','SUSPEND_CUSTOMERS','VIEW_ADMIN_CUSTOMERS','VIEW_KYC_DOCUMENTS','APPROVE_KYC','REJECT_KYC','VIEW_DASHBOARD_KYC_REVIEWS'], retail:[] },
  'admin-accounts':        { admin:['VIEW_ACCOUNTS','EDIT_ACCOUNTS','FREEZE_ACCOUNTS','ADJUST_BALANCES','CLOSE_ACCOUNTS','VIEW_ACCOUNT_HISTORY','VIEW_ADMIN_ACCOUNTS','VIEW_DASHBOARD_TOTAL_ACCOUNTS'], retail:[] },
  'admin-txns':            { admin:['VIEW_TRANSACTIONS','APPROVE_TRANSACTIONS','REJECT_TRANSACTIONS','REVERSE_TRANSACTIONS','VIEW_ADMIN_TRANSACTIONS','VIEW_DASHBOARD_PENDING_TRANSACTIONS'], retail:[] },
  'admin-compliance':      { admin:['VIEW_COMPLIANCE_ALERTS','MANAGE_COMPLIANCE_CASES','ACCESS_AML_TOOLS','CREATE_SAR_REPORTS','VIEW_ADMIN_COMPLIANCE','VIEW_ADMIN_KYC','VIEW_ADMIN_COMPLIANCE_BASIC','VIEW_ADMIN_COMPLIANCE_KYC_REVIEW','VIEW_DASHBOARD_COMPLIANCE_ALERTS'], retail:[] },
  'admin-reports':         { admin:['VIEW_ANALYTICS','GENERATE_REPORTS','EXPORT_DATA','VIEW_ADMIN_REPORTS','VIEW_ADMIN_ANALYTICS'], retail:[] },
  'admin-audit':           { admin:['ACCESS_AUDIT_LOGS','ACCESS_API_LOGS','VIEW_ADMIN_AUDIT'], retail:[] },
  'admin-users':           { admin:['MANAGE_ADMIN_USERS','VIEW_ADMIN_USERS'], retail:[] },
  'admin-settings':        { admin:['MANAGE_SETTINGS','CONFIGURE_SYSTEM','CONFIGURE_PRODUCTS','MANAGE_FEES','SET_INTEREST_RATES','VIEW_ADMIN_SETTINGS'], retail:[] },
  'admin-trading-bots':    { admin:['MANAGE_TRADING_BOTS','VIEW_TRADING_BOT_ANALYTICS','VIEW_TRADING_STRATEGY_LOGS','CONFIGURE_TRADING_STRATEGIES','EXECUTE_TRADING_ORDERS','VIEW_TRADING_BOT_PERFORMANCE','VIEW_ADMIN_TRADING_BOTS','VIEW_DASHBOARD_TRADING_BOTS'], retail:[] },
  'admin-kucoin':          { admin:['MANAGE_TRADING_BOTS','VIEW_TRADING_BOT_ANALYTICS','VIEW_ADMIN_TRADING_BOTS'], retail:[] },
  'admin-provider':        { admin:['MANAGE_PROVIDER_CONFIG','MANAGE_INTEGRATIONS','VIEW_ADMIN_INTEGRATIONS','VIEW_ADMIN_SETTINGS'], retail:[] },
  'admin-asset-tokens':    { admin:['VIEW_ASSET_TOKENS','MANAGE_ASSET_TOKENS','VIEW_ADMIN_ASSET_TOKENS'], retail:[] },
  'admin-stablecoin-mgmt': { admin:['VIEW_ADMIN_STABLECOIN','MANAGE_STABLECOIN_CONFIG'], retail:[] },
  'admin-order-book':      { admin:['MANAGE_ORDER_BOOK_PROVIDERS','VIEW_ORDER_BOOK_ANALYTICS','VIEW_ADMIN_ORDER_BOOK','CANCEL_ANY_ORDER','VIEW_ALL_TRADING_ORDERS'], retail:[] },
  'admin-eligibility':     { admin:['MANAGE_TRADING_BOTS','VIEW_ADMIN_TRADING_BOTS'], retail:[] },
  'admin-kyt':             { admin:['VIEW_KYT','VIEW_ADMIN_KYT'], retail:[] },
  'admin-risk':            { admin:['VIEW_ANALYTICS','VIEW_ADMIN_ANALYTICS'], retail:[] },
  'admin-health':          { admin:['CONFIGURE_SYSTEM','VIEW_ADMIN_SETTINGS','VIEW_DASHBOARD_SYSTEM_HEALTH','VIEW_DASHBOARD_SYSTEM_STATUS'], retail:[] },
  'admin-igaming':         { admin:['VIEW_ADMIN_IGAMING'], retail:[] },
  'admin-p2p':             { admin:['VIEW_ADMIN_P2PGAME'], retail:[] },
  'admin-social':          { admin:['MANAGE_SOCIAL_MEDIA_POSTS','VIEW_SOCIAL_MEDIA_ANALYTICS','SCHEDULE_SOCIAL_MEDIA_CONTENT','MODERATE_SOCIAL_MEDIA_COMMENTS','MANAGE_SOCIAL_MEDIA_CAMPAIGNS','VIEW_ADMIN_SOCIAL_MEDIA'], retail:[] },
  'admin-crypto-payouts':  { admin:['MANAGE_CRYPTO_PAYOUTS','CREATE_ADMIN_CRYPTO_WALLETS','EXECUTE_BATCH_CRYPTO_PAYOUTS','VIEW_CRYPTO_PAYOUT_HISTORY','VIEW_RETAIL_CRYPTO_WALLETS','VIEW_ADMIN_CRYPTOPAYOUTS'], retail:[] },
  'admin-provider-config': { admin:['MANAGE_INTEGRATIONS','MANAGE_PROVIDER_CONFIG','VIEW_ADMIN_INTEGRATIONS'], retail:[] },
  'admin-products':        { admin:['VIEW_ASSET_TOKENS','MANAGE_ASSET_TOKENS','CONFIGURE_PRODUCTS','VIEW_ADMIN_PRODUCTS'], retail:[] },
  'admin-review-queue':    { admin:['VIEW_COMPLIANCE_ALERTS','MANAGE_COMPLIANCE_CASES','VIEW_ADMIN_COMPLIANCE'], retail:[] },
  'admin-multisig':        { admin:['VIEW_CUSTOMERS','VIEW_ADMIN_CUSTOMERS'], retail:[] },
};

const BASE_ADMIN_PERMISSIONS = ['VIEW_ADMIN_DASHBOARD','VIEW_ADMIN_NOTIFICATIONS','VIEW_DASHBOARD_SYSTEM_HEALTH','VIEW_DASHBOARD_QUICK_INSIGHTS','VIEW_DASHBOARD_SYSTEM_STATUS'];
const BASE_RETAIL_PERMISSIONS = ['VIEW_RETAIL_DASHBOARD','VIEW_RETAIL_DASHBOARD_BALANCE','VIEW_RETAIL_DASHBOARD_QUICK_ACTIONS','VIEW_RETAIL_DASHBOARD_ACCOUNTS','VIEW_RETAIL_DASHBOARD_TRANSACTIONS','VIEW_RETAIL_NOTIFICATIONS','VIEW_RETAIL_PROFILE','VIEW_RETAIL_SETTINGS'];

/* ═══════════════════════════════════════════════════
   HELPERS
═══════════════════════════════════════════════════ */
const STATUS_LABEL: Record<string, string> = { live: '✅ Live', partial: '🔶 Partial', mock: '🧪 Mock', wip: '🚧 In Progress', todo: '📋 To Be Added' };
const STATUS_ICON: Record<string, string>  = { live: '✅', partial: '🔶', mock: '🧪', wip: '🚧', todo: '📋' };

function statusBadgeClass(status: string): string {
  const map: Record<string, string> = { live: 'b-live', partial: 'b-partial', mock: 'b-mock', wip: 'b-wip', todo: 'b-todo' };
  return map[status] || 'b-wip';
}

function escHtml(s: string): string {
  return s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
}

/* ═══════════════════════════════════════════════════
   COMPONENT
═══════════════════════════════════════════════════ */
const ClientFeatureSelector: React.FC = () => {
  const navigate = useNavigate();
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [activeFilter, setActiveFilter] = useState<FilterType>('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [collapsedCats, setCollapsedCats] = useState<Set<string>>(new Set());
  const [modalOpen, setModalOpen] = useState(false);
  const [modalTab, setModalTab] = useState<ModalTab>('spec');
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [clientName, setClientName] = useState('');
  const [clientNotes, setClientNotes] = useState('');
  const [adminPanelUrl, setAdminPanelUrl] = useState('http://localhost:3000');
  const [toast, setToast] = useState<{ msg: string; visible: boolean }>({ msg: '', visible: false });
  const toastTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  /* ── Toast ── */
  const showToast = useCallback((msg: string) => {
    setToast({ msg, visible: true });
    if (toastTimer.current) clearTimeout(toastTimer.current);
    toastTimer.current = setTimeout(() => setToast(t => ({ ...t, visible: false })), 2500);
  }, []);

  /* ── Feature lookup ── */
  const featById = useCallback((id: string) => ALL_FEATURES.find(f => f.id === id), []);

  /* ── Toggle feature ── */
  const toggleFeature = useCallback((id: string, checked: boolean) => {
    setSelected(prev => {
      const next = new Set(prev);
      if (checked) {
        // Auto-enable dependencies
        const feat = ALL_FEATURES.find(f => f.id === id);
        feat?.deps?.forEach(depId => next.add(depId));
        next.add(id);
      } else {
        next.delete(id);
      }
      return next;
    });
  }, []);

  /* ── Toggle all in category ── */
  const toggleAllInCat = useCallback((catId: string) => {
    const cat = CATEGORIES.find(c => c.id === catId);
    if (!cat) return;
    const visibleFeats = cat.features.filter(f => isFeatureVisible(f, searchQuery, activeFilter));
    const allChecked = visibleFeats.every(f => selected.has(f.id));
    setSelected(prev => {
      const next = new Set(prev);
      visibleFeats.forEach(f => allChecked ? next.delete(f.id) : next.add(f.id));
      return next;
    });
  }, [selected, searchQuery, activeFilter]);

  /* ── Visibility check ── */
  function isFeatureVisible(feat: Feature, q: string, filter: FilterType): boolean {
    const matchSearch = !q || feat.name.toLowerCase().includes(q.toLowerCase()) || feat.desc.toLowerCase().includes(q.toLowerCase());
    const matchFilter = filter === 'all' || feat.status === filter;
    return matchSearch && matchFilter;
  }

  /* ── Apply preset ── */
  const applyPreset = useCallback((name: string) => {
    const ids = PRESETS[name] || [];
    setSelected(new Set(ids));
    showToast(`✅ Preset applied — ${ids.length} features selected`);
  }, [showToast]);

  /* ── Clear all ── */
  const clearAll = useCallback(() => {
    setSelected(new Set());
    showToast('🗑 All features cleared');
  }, [showToast]);

  /* ── Derived permissions ── */
  const derivePermissions = useCallback(() => {
    const adminSet = new Set(BASE_ADMIN_PERMISSIONS);
    const retailSet = new Set(BASE_RETAIL_PERMISSIONS);
    selected.forEach(id => {
      const map = FEATURE_PERMISSION_MAP[id];
      if (map) {
        map.admin.forEach(p => adminSet.add(p));
        map.retail.forEach(p => retailSet.add(p));
      }
    });
    return { adminPerms: Array.from(adminSet), retailPerms: Array.from(retailSet) };
  }, [selected]);

  /* ── Build admin setup URL ── */
  const buildAdminSetupURL = useCallback(() => {
    const base = adminPanelUrl.replace(/\/$/, '');
    const { adminPerms, retailPerms } = derivePermissions();
    const data = {
      features: Array.from(selected),
      adminPermissions: adminPerms,
      retailPermissions: retailPerms,
      source: 'CLIENT_FEATURE_SELECTOR',
      generatedAt: new Date().toISOString(),
      totalFeatures: selected.size,
    };
    const encoded = btoa(JSON.stringify(data));
    return `${base}/admin/dashboard?tab=clientOnboarding&data=${encodeURIComponent(encoded)}`;
  }, [adminPanelUrl, derivePermissions, selected]);

  /* ── Summary stats ── */
  const summaryData = useMemo(() => {
    const selectedFeats = ALL_FEATURES.filter(f => selected.has(f.id));
    const byCategory = CATEGORIES
      .map(cat => ({ cat, feats: cat.features.filter(f => selected.has(f.id)) }))
      .filter(x => x.feats.length > 0);
    const liveCount = selectedFeats.filter(f => f.status === 'live').length;
    const partialCount = selectedFeats.filter(f => f.status === 'partial').length;
    return { selectedFeats, byCategory, liveCount, partialCount };
  }, [selected]);

  /* ── Generate spec text ── */
  const generateSpecText = useCallback(() => {
    const client = clientName || 'Unnamed Client';
    const date = new Date().toLocaleString('en-GB');
    const lines: string[] = [];
    lines.push('═══════════════════════════════════════════════════════════');
    lines.push('  ALL-IN-ONE PLATFORM — CLIENT FEATURE SPECIFICATION');
    lines.push('═══════════════════════════════════════════════════════════');
    lines.push('');
    lines.push(`  Client / Project : ${client}`);
    lines.push(`  Generated        : ${date}`);
    lines.push(`  Total Features   : ${selected.size}`);
    if (clientNotes) lines.push(`  Notes            : ${clientNotes}`);
    lines.push('');

    CATEGORIES.forEach(cat => {
      const feats = cat.features.filter(f => selected.has(f.id));
      if (!feats.length) return;
      lines.push('───────────────────────────────────────────────────────────');
      lines.push(`  ${cat.icon}  ${cat.label.toUpperCase()}`);
      lines.push('───────────────────────────────────────────────────────────');
      feats.forEach(f => {
        const statusStr = f.status === 'live' ? '✅ LIVE' : f.status === 'partial' ? '🔶 PARTIAL' : '🧪 MOCK/SHELL';
        lines.push('');
        lines.push(`  ▸ ${f.name}`);
        lines.push(`    Status  : ${statusStr}`);
        lines.push(`    Details : ${f.desc}`);
        if (f.adminNote) lines.push(`    Admin   : ${f.adminNote}`);
        if (f.deps.length) {
          const depNames = f.deps.map(d => featById(d)?.name || d);
          lines.push(`    Depends : ${depNames.join(', ')}`);
        }
      });
      lines.push('');
    });

    const all = ALL_FEATURES.filter(f => selected.has(f.id));
    const live = all.filter(f => f.status === 'live');
    const partial = all.filter(f => f.status === 'partial');
    const mock = all.filter(f => f.status === 'mock');
    const adminSetup = all.filter(f => f.adminRequired);

    lines.push('═══════════════════════════════════════════════════════════');
    lines.push('  IMPLEMENTATION SUMMARY');
    lines.push('═══════════════════════════════════════════════════════════');
    lines.push('');
    lines.push(`  ✅ Live & Ready  : ${live.length} features`);
    if (live.length) lines.push(`     ${live.map(f => f.name).join(', ')}`);
    lines.push('');
    lines.push(`  🔶 Partial       : ${partial.length} features (need completion)`);
    if (partial.length) lines.push(`     ${partial.map(f => f.name).join(', ')}`);
    lines.push('');
    lines.push(`  🧪 Mock/Shell    : ${mock.length} features (need full implementation)`);
    if (mock.length) lines.push(`     ${mock.map(f => f.name).join(', ')}`);
    lines.push('');
    lines.push(`  ⚙️  Admin Setup  : ${adminSetup.length} features require admin configuration`);
    if (adminSetup.length) lines.push(`     ${adminSetup.map(f => f.name).join(', ')}`);
    lines.push('');
    lines.push('═══════════════════════════════════════════════════════════');
    lines.push('  Generated by All-in-One Platform — Client Feature Selector');
    lines.push('═══════════════════════════════════════════════════════════');

    return lines.join('\n');
  }, [clientName, clientNotes, selected, featById]);

  /* ── Copy text ── */
  const copySpecText = useCallback(() => {
    const text = generateSpecText();
    navigator.clipboard.writeText(text)
      .then(() => showToast('📋 Spec copied to clipboard!'))
      .catch(() => showToast('📋 Spec copied!'));
  }, [generateSpecText, showToast]);

  /* ── Copy JSON ── */
  const copyJSON = useCallback(() => {
    if (!selected.size) { showToast('⚠️ No features selected'); return; }
    const all = ALL_FEATURES.filter(f => selected.has(f.id));
    const out = {
      generated: new Date().toISOString(),
      totalFeatures: all.length,
      features: all.map(f => ({ id: f.id, name: f.name, status: f.status, adminRequired: f.adminRequired, dependencies: f.deps })),
      byCategory: Object.fromEntries(
        CATEGORIES.map(c => [c.id, c.features.filter(f => selected.has(f.id)).map(f => f.id)]).filter(([, v]) => (v as string[]).length > 0)
      ),
    };
    const txt = JSON.stringify(out, null, 2);
    navigator.clipboard.writeText(txt)
      .then(() => showToast('📋 JSON copied!'))
      .catch(() => showToast('📋 JSON copied!'));
  }, [selected, showToast]);

  /* ── Copy setup URL ── */
  const copySetupURL = useCallback(() => {
    if (!selected.size) { showToast('⚠️ No features selected'); return; }
    const url = buildAdminSetupURL();
    navigator.clipboard.writeText(url)
      .then(() => showToast('📋 Setup URL copied!'))
      .catch(() => showToast('📋 Setup URL copied!'));
  }, [selected.size, buildAdminSetupURL, showToast]);

  /* ── Build internal onboarding navigation path (no base URL needed) ── */
  const buildOnboardingNavPath = useCallback(() => {
    const { adminPerms, retailPerms } = derivePermissions();
    const data = {
      features: Array.from(selected),
      adminPermissions: adminPerms,
      retailPermissions: retailPerms,
      source: 'CLIENT_FEATURE_SELECTOR',
      generatedAt: new Date().toISOString(),
      totalFeatures: selected.size,
    };
    const encoded = btoa(JSON.stringify(data));
    return `/admin/dashboard?tab=clientOnboarding&data=${encodeURIComponent(encoded)}`;
  }, [derivePermissions, selected]);

  /* ── Launch onboarding — navigate internally within the admin panel ── */
  const launchAdminSetup = useCallback(() => {
    if (!selected.size) { showToast('⚠️ Please select features first'); return; }
    const path = buildOnboardingNavPath();
    setModalOpen(false);
    navigate(path);
    showToast('🚀 Switching to Client Onboarding Wizard…');
  }, [selected.size, buildOnboardingNavPath, navigate, showToast]);

  /* ── Download spec ── */
  const downloadSpec = useCallback(() => {
    const client = (clientName || 'client').replace(/\s+/g, '-').toLowerCase();
    const date = new Date().toISOString().split('T')[0];
    const dateDisplay = new Date().toLocaleDateString('en-GB', { day: '2-digit', month: 'long', year: 'numeric' });
    const all = ALL_FEATURES.filter(f => selected.has(f.id));

    let catHtml = '';
    CATEGORIES.forEach(cat => {
      const feats = cat.features.filter(f => selected.has(f.id));
      if (!feats.length) return;
      catHtml += `<div style="margin-bottom:28px">
        <div style="background:#f3f4f6;border-radius:8px 8px 0 0;padding:12px 16px;border:1px solid #e5e7eb;display:flex;align-items:center;gap:10px">
          <span style="font-size:20px">${cat.icon}</span>
          <div><div style="font-weight:700;font-size:15px">${cat.label}</div><div style="font-size:11px;color:#6b7280">${cat.subtitle}</div></div>
          <span style="margin-left:auto;background:#0a74da;color:#fff;border-radius:20px;padding:2px 10px;font-size:12px;font-weight:700">${feats.length} selected</span>
        </div>`;
      feats.forEach(f => {
        const statusBg: Record<string, string> = { live: '#d1fae5', partial: '#fef3c7', mock: '#fee2e2' };
        const statusLbl: Record<string, string> = { live: '✅ Live', partial: '🔶 Partial', mock: '🧪 Mock/Shell' };
        catHtml += `<div style="border:1px solid #e5e7eb;border-top:none;padding:14px 16px;background:#fff">
          <div style="font-weight:700;font-size:13px;margin-bottom:4px">${escHtml(f.name)}
            <span style="background:${statusBg[f.status] || '#f3f4f6'};border-radius:20px;padding:2px 9px;font-size:10px;font-weight:700;margin-left:6px">${statusLbl[f.status] || f.status}</span>
            ${f.adminRequired ? '<span style="background:#ede9fe;color:#5b21b6;border-radius:20px;padding:2px 9px;font-size:10px;font-weight:700;margin-left:4px">Admin Setup</span>' : ''}
          </div>
          <div style="font-size:12px;color:#6b7280;line-height:1.6">${escHtml(f.desc)}</div>
          ${f.adminNote ? `<div style="font-size:11px;color:#7c3aed;margin-top:4px">ℹ️ ${escHtml(f.adminNote)}</div>` : ''}
        </div>`;
      });
      catHtml += '</div>';
    });

    const html = `<!DOCTYPE html><html lang="en"><head><meta charset="UTF-8"/><meta name="viewport" content="width=device-width,initial-scale=1.0"/>
<title>Client Spec — ${escHtml(clientName || 'Client')}</title>
<style>body{font-family:'Segoe UI',system-ui,sans-serif;background:#f3f4f6;color:#111827;margin:0;padding:32px 16px}.container{max-width:860px;margin:0 auto}.header{background:linear-gradient(135deg,#0a74da,#075faf);color:#fff;border-radius:12px;padding:32px;margin-bottom:28px}.header h1{font-size:24px;margin-bottom:6px}.header p{opacity:.8;font-size:14px}.stats{display:flex;gap:12px;margin-top:16px;flex-wrap:wrap}.stat{background:rgba(255,255,255,.15);border-radius:8px;padding:8px 16px;text-align:center}.stat .n{font-size:22px;font-weight:800;display:block}.stat .l{font-size:11px;opacity:.8}</style></head>
<body><div class="container">
<div class="header">
  <h1>📋 Client Feature Specification</h1>
  <p><strong>${escHtml(clientName || 'Client')}</strong> — Generated ${dateDisplay}</p>
  ${clientNotes ? `<p style="margin-top:8px;font-style:italic;opacity:.8">${escHtml(clientNotes)}</p>` : ''}
  <div class="stats">
    <div class="stat"><span class="n">${all.length}</span><span class="l">Total</span></div>
    <div class="stat"><span class="n">${all.filter(f => f.status === 'live').length}</span><span class="l">✅ Live</span></div>
    <div class="stat"><span class="n">${all.filter(f => f.status === 'partial').length}</span><span class="l">🔶 Partial</span></div>
    <div class="stat"><span class="n">${all.filter(f => f.status === 'mock').length}</span><span class="l">🧪 Mock</span></div>
  </div>
</div>
${catHtml}
<div style="text-align:center;padding:24px 0;color:#9ca3af;font-size:12px">All-in-One Platform — Client Feature Specification — ${dateDisplay}</div>
</div></body></html>`;

    const blob = new Blob([html], { type: 'text/html' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = `feature-spec-${client}-${date}.html`;
    a.click();
    URL.revokeObjectURL(a.href);
    showToast('⬇ Spec downloaded!');
  }, [clientName, clientNotes, selected, showToast]);

  /* ── Open modal ── */
  const openGenerateModal = useCallback((tab: ModalTab = 'spec') => {
    if (!selected.size) { showToast('⚠️ Please select at least one feature'); return; }
    setModalTab(tab);
    setModalOpen(true);
  }, [selected.size, showToast]);

  /* ── Quick launch (topbar button) — navigate directly, no modal ── */
  const quickLaunchAdminSetup = useCallback(() => {
    if (!selected.size) {
      openGenerateModal('setup');
      return;
    }
    const path = buildOnboardingNavPath();
    navigate(path);
    showToast('🚀 Switching to Client Onboarding Wizard…');
  }, [selected.size, openGenerateModal, buildOnboardingNavPath, navigate, showToast]);

  /* ═════════════════════════════════════════════
     RENDER
  ═════════════════════════════════════════════ */

  /* Summary panel content (shared between sidebar and mobile drawer) */
  const renderSummaryContent = () => {
    const { byCategory } = summaryData;
    if (!selected.size) {
      return (
        <div style={{ padding: '32px 16px', textAlign: 'center', color: '#6b7280', fontSize: 13 }}>
          <div style={{ fontSize: 32, marginBottom: 8, opacity: 0.3 }}>☑️</div>
          <p>Toggle features on the left<br />to build your client spec</p>
        </div>
      );
    }
    return (
      <div>
        {byCategory.map(({ cat, feats }) => (
          <div key={cat.id} style={{ borderBottom: '1px solid #e5e7eb' }}>
            <div style={{ padding: '10px 14px', fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.7px', color: '#6b7280', background: '#fafafa', display: 'flex', justifyContent: 'space-between' }}>
              <span>{cat.icon} {cat.label}</span>
              <span>{feats.length}</span>
            </div>
            {feats.map(f => (
              <div key={f.id} style={{ padding: '8px 14px', display: 'flex', alignItems: 'center', gap: 8, borderBottom: '1px solid #f3f4f6', fontSize: 12 }}>
                <span style={{ fontSize: 14 }}>{STATUS_ICON[f.status] || '🔧'}</span>
                <span style={{ flex: 1, fontWeight: 600 }}>{f.name}</span>
                <span style={{ ...badgeStyle(statusBadgeClass(f.status)), fontSize: 9 }}>{f.status}</span>
              </div>
            ))}
          </div>
        ))}
      </div>
    );
  };

  /* Admin setup preview */
  const { adminPerms, retailPerms } = derivePermissions();
  const setupUrl = selected.size ? buildAdminSetupURL() : '';

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%', fontFamily: "'Segoe UI', system-ui, sans-serif" }}>

      {/* ── INLINE STYLES ── */}
      <style>{`
        .cfs-toggle { position: relative; display: inline-block; width: 42px; height: 24px; flex-shrink: 0; }
        .cfs-toggle input { opacity: 0; width: 0; height: 0; position: absolute; }
        .cfs-slider { position: absolute; inset: 0; background: #d1d5db; border-radius: 24px; cursor: pointer; transition: background .2s; }
        .cfs-slider:before { content:''; position: absolute; height: 18px; width: 18px; left: 3px; bottom: 3px; background: #fff; border-radius: 50%; transition: transform .2s; box-shadow: 0 1px 3px rgba(0,0,0,.2); }
        .cfs-toggle input:checked + .cfs-slider { background: #0a74da; }
        .cfs-toggle input:checked + .cfs-slider:before { transform: translateX(18px); }
        .cfs-feat:hover { background: #fafafa; }
        .cfs-feat.selected { background: #f0f7ff; }
        .cfs-filter-pill { border: 1px solid #e5e7eb; background: #fff; border-radius: 20px; padding: 5px 12px; font-size: 12px; font-weight: 600; cursor: pointer; transition: all .15s; color: #6b7280; white-space: nowrap; }
        .cfs-filter-pill.active { background: #0a74da; border-color: #0a74da; color: #fff; }
        .cfs-preset-btn { border: 1.5px solid #e5e7eb; background: #fff; border-radius: 8px; padding: 8px 14px; font-size: 12px; font-weight: 600; cursor: pointer; transition: all .15s; display: flex; align-items: center; gap: 6px; color: #111827; }
        .cfs-preset-btn:hover { border-color: #0a74da; background: #eff6ff; color: #0a74da; }
        .cfs-topbar-btn { background: rgba(255,255,255,.15); border: 1px solid rgba(255,255,255,.3); color: #fff; border-radius: 7px; padding: 6px 14px; font-size: 13px; font-weight: 600; cursor: pointer; transition: background .15s; white-space: nowrap; }
        .cfs-topbar-btn:hover { background: rgba(255,255,255,.25); }
        .cfs-topbar-btn.accent { background: #fff; color: #0a74da; }
        .cfs-topbar-btn.accent:hover { background: #f0f7ff; }
        .cfs-modal-overlay { position: fixed; inset: 0; background: rgba(0,0,0,.55); z-index: 1000; display: flex; align-items: center; justify-content: center; padding: 16px; backdrop-filter: blur(3px); }
        .cfs-modal { background: #fff; border-radius: 14px; max-width: 760px; width: 100%; max-height: 90vh; display: flex; flex-direction: column; box-shadow: 0 20px 60px rgba(0,0,0,.25); overflow: hidden; }
        .cfs-drawer-overlay { position: fixed; inset: 0; z-index: 900; display: flex; flex-direction: row; }
        .cfs-sum-action-btn { width: 100%; padding: 9px 14px; border-radius: 8px; font-size: 13px; font-weight: 600; cursor: pointer; border: none; transition: all .15s; display: flex; align-items: center; justify-content: center; gap: 6px; }
        @media (max-width: 900px) { .cfs-right-panel { display: none !important; } .cfs-mob-bar { display: flex !important; } }
        @media (min-width: 901px) { .cfs-mob-bar { display: none !important; } }
      `}</style>

      {/* ── TOP BAR ── */}
      <div style={{ background: 'linear-gradient(135deg,#0a74da 0%,#075faf 100%)', color: '#fff', padding: '0 24px', height: 58, display: 'flex', alignItems: 'center', justifyContent: 'space-between', boxShadow: '0 2px 12px rgba(10,116,218,.35)', flexShrink: 0 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <span style={{ fontSize: 20 }}>⚙️</span>
          <div>
            <div style={{ fontSize: 16, fontWeight: 700, letterSpacing: '.3px' }}>Client Feature Selector</div>
            <div style={{ fontSize: 12, opacity: .75 }}>All-in-One Banking &amp; Trading Platform</div>
          </div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
          <div style={{ background: 'rgba(255,255,255,.2)', border: '1px solid rgba(255,255,255,.3)', borderRadius: 20, padding: '3px 12px', fontSize: 12, fontWeight: 700 }}>
            {selected.size} feature{selected.size !== 1 ? 's' : ''} selected
          </div>
          <button className="cfs-topbar-btn" onClick={clearAll}>Clear All</button>
          <button className="cfs-topbar-btn accent" onClick={() => openGenerateModal('spec')}>📄 Generate Spec</button>
          <button className="cfs-topbar-btn" style={{ background: '#7c3aed', borderColor: '#7c3aed' }} onClick={quickLaunchAdminSetup} title="Launch Onboarding Wizard in Admin Panel">🚀 Admin Setup</button>
        </div>
      </div>

      {/* ── MOBILE BAR ── */}
      <div className="cfs-mob-bar" style={{ background: '#1e1e2d', color: '#fff', padding: '0 16px', height: 48, alignItems: 'center', justifyContent: 'space-between' }}>
        <span style={{ fontSize: 12, fontWeight: 600, opacity: .8 }}>Select features below</span>
        <button onClick={() => setDrawerOpen(true)} style={{ background: 'rgba(255,255,255,.12)', border: '1px solid rgba(255,255,255,.2)', color: '#fff', borderRadius: 7, padding: '5px 12px', fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>
          📋 View Summary ({selected.size})
        </button>
      </div>

      {/* ── LAYOUT ── */}
      <div style={{ display: 'flex', flex: 1, overflow: 'hidden' }}>

        {/* LEFT: FEATURE LIST */}
        <div style={{ flex: 1, overflowY: 'auto', padding: '20px 20px 40px' }}>

          {/* Controls */}
          <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 16, flexWrap: 'wrap' }}>
            <input
              type="text"
              placeholder="🔍  Search features…"
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
              style={{ flex: 1, minWidth: 180, border: '1px solid #e5e7eb', borderRadius: 8, padding: '8px 12px', fontSize: 13, outline: 'none', background: '#fff' }}
            />
            {(['all','live','partial','mock'] as FilterType[]).map(f => (
              <button
                key={f}
                className={`cfs-filter-pill${activeFilter === f ? ' active' : ''}`}
                onClick={() => setActiveFilter(f)}
              >
                {f === 'all' ? 'All' : f === 'live' ? '✅ Live' : f === 'partial' ? '🔶 Partial' : '🧪 Mock'}
              </button>
            ))}
          </div>

          {/* Presets */}
          <div style={{ display: 'flex', gap: 8, marginBottom: 20, flexWrap: 'wrap', alignItems: 'center' }}>
            <strong style={{ fontSize: 12, color: '#6b7280', marginRight: 2 }}>Presets:</strong>
            {[
              { key: 'basic',   label: '🏦 Basic Banking',  count: PRESETS.basic.length },
              { key: 'trading', label: '📈 Trading Suite',  count: PRESETS.trading.length },
              { key: 'crypto',  label: '₿ Crypto Focus',    count: PRESETS.crypto.length },
              { key: 'gaming',  label: '🎮 Gaming Bundle',  count: PRESETS.gaming.length },
              { key: 'full',    label: '🌟 Full Suite',     count: PRESETS.full.length },
            ].map(p => (
              <button key={p.key} className="cfs-preset-btn" onClick={() => applyPreset(p.key)}>
                {p.label}
                <span style={{ background: '#f3f4f6', borderRadius: 10, padding: '1px 7px', fontSize: 10, fontWeight: 700, color: '#6b7280' }}>{p.key === 'full' ? 'All' : p.count}</span>
              </button>
            ))}
          </div>

          {/* Categories */}
          {CATEGORIES.map(cat => {
            const visibleFeats = cat.features.filter(f => isFeatureVisible(f, searchQuery, activeFilter));
            if (!visibleFeats.length) return null;
            const checkedCount = cat.features.filter(f => selected.has(f.id)).length;
            const isCollapsed = collapsedCats.has(cat.id);

            return (
              <div key={cat.id} style={{ marginBottom: 24 }}>
                {/* Category header */}
                <div
                  onClick={() => setCollapsedCats(prev => { const next = new Set(prev); next.has(cat.id) ? next.delete(cat.id) : next.add(cat.id); return next; })}
                  style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '10px 14px', background: '#fff', border: '1px solid #e5e7eb', borderRadius: isCollapsed ? 10 : '10px 10px 0 0', cursor: 'pointer', userSelect: 'none' }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                    <div style={{ width: 32, height: 32, borderRadius: 8, background: cat.color, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 16 }}>{cat.icon}</div>
                    <div>
                      <div style={{ fontSize: 14, fontWeight: 700 }}>{cat.label}</div>
                      <div style={{ fontSize: 11, color: '#6b7280' }}>{cat.subtitle}</div>
                    </div>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <span style={{ background: checkedCount > 0 ? '#0a74da' : '#f3f4f6', color: checkedCount > 0 ? '#fff' : '#6b7280', borderRadius: 20, padding: '2px 9px', fontSize: 11, fontWeight: 700 }}>
                      {checkedCount}/{cat.features.length}
                    </span>
                    <button
                      onClick={e => { e.stopPropagation(); toggleAllInCat(cat.id); }}
                      style={{ fontSize: 11, fontWeight: 600, color: '#0a74da', background: 'none', border: '1px solid #e5e7eb', borderRadius: 6, padding: '3px 9px', cursor: 'pointer' }}
                    >
                      Select All
                    </button>
                    <span style={{ fontSize: 12, color: '#6b7280', transform: isCollapsed ? 'rotate(-90deg)' : 'none', transition: 'transform .2s', display: 'inline-block' }}>▾</span>
                  </div>
                </div>

                {/* Feature grid */}
                {!isCollapsed && (
                  <div style={{ border: '1px solid #e5e7eb', borderTop: 'none', borderRadius: '0 0 10px 10px', display: 'grid', gridTemplateColumns: 'repeat(auto-fill,minmax(280px,1fr))' }}>
                    {visibleFeats.map(feat => {
                      const isSelected = selected.has(feat.id);
                      const depNames = feat.deps.map(d => featById(d)?.name || d);
                      return (
                        <div
                          key={feat.id}
                          className={`cfs-feat${isSelected ? ' selected' : ''}`}
                          style={{ padding: '14px 16px', borderRight: '1px solid #e5e7eb', borderBottom: '1px solid #e5e7eb', transition: 'background .15s', position: 'relative' }}
                        >
                          <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 8 }}>
                            <div style={{ flex: 1, minWidth: 0 }}>
                              <div style={{ fontSize: 13, fontWeight: 700, marginBottom: 3 }}>{feat.name}</div>
                              <div style={{ fontSize: 12, color: '#6b7280', lineHeight: 1.5 }}>{feat.desc}</div>
                              {feat.adminNote && (
                                <div style={{ fontSize: 11, color: '#7c3aed', marginTop: 6, display: 'flex', alignItems: 'center', gap: 4 }}>ℹ️ {feat.adminNote}</div>
                              )}
                            </div>
                            <label className="cfs-toggle">
                              <input
                                type="checkbox"
                                checked={isSelected}
                                onChange={e => toggleFeature(feat.id, e.target.checked)}
                              />
                              <span className="cfs-slider"></span>
                            </label>
                          </div>
                          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, marginTop: 8 }}>
                            <span style={badgeStyle(statusBadgeClass(feat.status))}>{STATUS_LABEL[feat.status]}</span>
                            {feat.adminRequired && <span style={badgeStyle('b-admin')}>Admin Setup</span>}
                            {depNames.map(dep => (
                              <span key={dep} style={badgeStyle('b-dep')}>Needs: {dep}</span>
                            ))}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {/* RIGHT: SUMMARY PANEL */}
        <div className="cfs-right-panel" style={{ width: 340, flexShrink: 0, background: '#fff', borderLeft: '1px solid #e5e7eb', overflowY: 'auto', display: 'flex', flexDirection: 'column' }}>
          {/* Header */}
          <div style={{ padding: 16, borderBottom: '1px solid #e5e7eb', background: 'linear-gradient(135deg,#1e1e2d,#2d2d3f)', color: '#fff', flexShrink: 0 }}>
            <div style={{ fontSize: 14, fontWeight: 700, marginBottom: 2 }}>📋 Selection Summary</div>
            <div style={{ fontSize: 11, opacity: .7 }}>Updates live as you select features</div>
            <div style={{ display: 'flex', gap: 8, marginTop: 10 }}>
              {[
                { num: selected.size, label: 'Selected' },
                { num: summaryData.byCategory.length, label: 'Categories' },
                { num: summaryData.liveCount, label: 'Live' },
                { num: summaryData.partialCount, label: 'Partial' },
              ].map(s => (
                <div key={s.label} style={{ background: 'rgba(255,255,255,.12)', border: '1px solid rgba(255,255,255,.15)', borderRadius: 8, padding: '4px 10px', fontSize: 11, fontWeight: 600, textAlign: 'center', flex: 1 }}>
                  <div style={{ fontSize: 16, fontWeight: 800 }}>{s.num}</div>
                  {s.label}
                </div>
              ))}
            </div>
          </div>

          {/* Body */}
          <div style={{ flex: 1, overflowY: 'auto' }}>{renderSummaryContent()}</div>

          {/* Footer */}
          <div style={{ borderTop: '1px solid #e5e7eb', padding: 14, flexShrink: 0, display: 'flex', flexDirection: 'column', gap: 8 }}>
            <button className="cfs-sum-action-btn" style={{ background: '#0a74da', color: '#fff' }} onClick={() => openGenerateModal('spec')}>📄 Generate Client Spec</button>
            <button className="cfs-sum-action-btn" style={{ background: '#fff', color: '#111827', border: '1px solid #e5e7eb' }} onClick={copyJSON}>📋 Copy JSON</button>
            <button className="cfs-sum-action-btn" style={{ background: '#fef2f2', color: '#dc2626', border: '1px solid #fca5a5' }} onClick={clearAll}>🗑 Clear All</button>
          </div>
        </div>
      </div>

      {/* ── MOBILE DRAWER ── */}
      {drawerOpen && (
        <div className="cfs-drawer-overlay">
          <div style={{ flex: 1, background: 'rgba(0,0,0,.5)' }} onClick={() => setDrawerOpen(false)} />
          <div style={{ width: 'min(320px,100vw)', background: '#fff', overflowY: 'auto', display: 'flex', flexDirection: 'column', boxShadow: '-4px 0 24px rgba(0,0,0,.2)' }}>
            <div style={{ padding: 16, background: 'linear-gradient(135deg,#1e1e2d,#2d2d3f)', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexShrink: 0 }}>
              <div>
                <div style={{ fontSize: 14, fontWeight: 700 }}>📋 Summary</div>
                <div style={{ fontSize: 11, opacity: .7 }}>Selected features</div>
              </div>
              <button onClick={() => setDrawerOpen(false)} style={{ background: 'rgba(255,255,255,.15)', border: 'none', color: '#fff', borderRadius: 7, padding: '6px 12px', cursor: 'pointer', fontSize: 13 }}>✕ Close</button>
            </div>
            <div style={{ flex: 1, overflowY: 'auto' }}>{renderSummaryContent()}</div>
            <div style={{ padding: 14, borderTop: '1px solid #e5e7eb', display: 'flex', flexDirection: 'column', gap: 8 }}>
              <button className="cfs-sum-action-btn" style={{ background: '#0a74da', color: '#fff' }} onClick={() => { openGenerateModal('spec'); setDrawerOpen(false); }}>📄 Generate Spec</button>
              <button className="cfs-sum-action-btn" style={{ background: '#fff', color: '#111827', border: '1px solid #e5e7eb' }} onClick={() => { copyJSON(); setDrawerOpen(false); }}>📋 Copy JSON</button>
            </div>
          </div>
        </div>
      )}

      {/* ── GENERATE MODAL ── */}
      {modalOpen && (
        <div className="cfs-modal-overlay" onClick={e => { if (e.target === e.currentTarget) setModalOpen(false); }}>
          <div className="cfs-modal">
            {/* Header */}
            <div style={{ padding: '20px 24px 16px', borderBottom: '1px solid #e5e7eb', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexShrink: 0 }}>
              <h2 style={{ fontSize: 18, fontWeight: 700, margin: 0 }}>📄 Generate Client Specification</h2>
              <button onClick={() => setModalOpen(false)} style={{ background: 'none', border: 'none', fontSize: 20, cursor: 'pointer', color: '#6b7280', lineHeight: 1, padding: 4, borderRadius: 6 }}>✕</button>
            </div>

            {/* Tabs */}
            <div style={{ display: 'flex', borderBottom: '2px solid #e5e7eb', padding: '0 24px' }}>
              {([['spec','📄 Spec Generator'],['setup','🚀 Admin Panel Setup']] as [ModalTab,string][]).map(([tab, label]) => (
                <button
                  key={tab}
                  onClick={() => setModalTab(tab)}
                  style={{ padding: '10px 20px', background: 'none', border: 'none', borderBottom: `2px solid ${modalTab === tab ? (tab === 'spec' ? '#086afb' : '#7c3aed') : 'transparent'}`, marginBottom: -2, fontWeight: modalTab === tab ? 700 : 600, fontSize: 13, color: modalTab === tab ? (tab === 'spec' ? '#086afb' : '#7c3aed') : '#9ca3af', cursor: 'pointer' }}
                >
                  {label}
                </button>
              ))}
            </div>

            {/* Body */}
            <div style={{ flex: 1, overflowY: 'auto', padding: 24 }}>
              {/* TAB 1: Spec */}
              {modalTab === 'spec' && (
                <div>
                  <div style={{ marginBottom: 20 }}>
                    <label style={{ display: 'block', fontSize: 13, fontWeight: 600, marginBottom: 6 }}>Client / Project Name</label>
                    <input
                      type="text"
                      placeholder="e.g. Acme Bank, Project Phoenix…"
                      value={clientName}
                      onChange={e => setClientName(e.target.value)}
                      style={{ width: '100%', border: '1px solid #e5e7eb', borderRadius: 8, padding: '9px 12px', fontSize: 13, outline: 'none', boxSizing: 'border-box' }}
                    />
                    <label style={{ display: 'block', fontSize: 13, fontWeight: 600, marginBottom: 6, marginTop: 10 }}>Additional Notes (optional)</label>
                    <textarea
                      placeholder="Any additional context, special requirements, or delivery notes…"
                      value={clientNotes}
                      onChange={e => setClientNotes(e.target.value)}
                      style={{ width: '100%', border: '1px solid #e5e7eb', borderRadius: 8, padding: '9px 12px', fontSize: 13, outline: 'none', height: 70, resize: 'vertical', fontFamily: 'inherit', boxSizing: 'border-box' }}
                    />
                  </div>

                  {/* Spec preview */}
                  <div style={{ background: '#fafafa', border: '1px solid #e5e7eb', borderRadius: 10, padding: 16 }}>
                    <h3 style={{ fontSize: 14, fontWeight: 700, marginBottom: 12 }}>📋 Specification Preview</h3>
                    {[
                      ['Client / Project', clientName || '(Client Name)'],
                      ['Generated', new Date().toLocaleDateString('en-GB', { day: '2-digit', month: 'long', year: 'numeric' })],
                      ['Total Features', String(selected.size)],
                      ['Categories', String(summaryData.byCategory.length)],
                      ['✅ Live / Ready', String(summaryData.liveCount)],
                      ['🔶 Partial (needs work)', String(summaryData.partialCount)],
                    ].map(([label, value]) => (
                      <div key={label} style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, padding: '4px 0', borderBottom: '1px solid #f3f4f6' }}>
                        <span style={{ color: '#6b7280', fontWeight: 500 }}>{label}</span>
                        <span style={{ fontWeight: 700 }}>{value}</span>
                      </div>
                    ))}
                    <div style={{ marginTop: 14 }}>
                      {summaryData.byCategory.map(({ cat, feats }) => (
                        <div key={cat.id} style={{ marginBottom: 8 }}>
                          <div style={{ fontSize: 12, fontWeight: 700, marginBottom: 4 }}>{cat.icon} {cat.label}</div>
                          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                            {feats.map(f => (
                              <span key={f.id} style={badgeStyle(statusBadgeClass(f.status))}>{f.name}</span>
                            ))}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              )}

              {/* TAB 2: Admin Setup */}
              {modalTab === 'setup' && (
                <div>
                  <div style={{ marginBottom: 16 }}>
                    <label style={{ fontSize: 12, fontWeight: 700, color: '#374151', display: 'block', marginBottom: 6 }}>🌐 Admin Panel URL</label>
                    <input
                      type="text"
                      value={adminPanelUrl}
                      onChange={e => setAdminPanelUrl(e.target.value)}
                      style={{ width: '100%', padding: '10px 14px', border: '1px solid #e5e7eb', borderRadius: 8, fontSize: 13, boxSizing: 'border-box', fontFamily: 'monospace' }}
                      placeholder="http://localhost:3000"
                    />
                    <div style={{ fontSize: 11, color: '#9ca3af', marginTop: 4 }}>Base URL only — <code>/admin/dashboard?tab=clientOnboarding&data=...</code> is appended automatically</div>
                  </div>

                  {!selected.size ? (
                    <div style={{ background: '#fef9c3', border: '1px solid #fde047', borderRadius: 8, padding: 14, fontSize: 13, color: '#92400e' }}>
                      ⚠️ No features selected. Select features first then use this tab to launch the onboarding wizard.
                    </div>
                  ) : (
                    <>
                      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 12, marginBottom: 16 }}>
                        <div style={{ background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: 10, padding: 14, textAlign: 'center' }}>
                          <div style={{ fontSize: 26, fontWeight: 800, color: '#16a34a' }}>{selected.size}</div>
                          <div style={{ fontSize: 11, color: '#6b7280', marginTop: 2 }}>Features</div>
                        </div>
                        <div style={{ background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: 10, padding: 14, textAlign: 'center' }}>
                          <div style={{ fontSize: 26, fontWeight: 800, color: '#1d4ed8' }}>{adminPerms.length}</div>
                          <div style={{ fontSize: 11, color: '#6b7280', marginTop: 2 }}>Admin Permissions</div>
                        </div>
                        <div style={{ background: '#fdf4ff', border: '1px solid #e9d5ff', borderRadius: 10, padding: 14, textAlign: 'center' }}>
                          <div style={{ fontSize: 26, fontWeight: 800, color: '#7c3aed' }}>{retailPerms.length}</div>
                          <div style={{ fontSize: 11, color: '#6b7280', marginTop: 2 }}>Retail Permissions</div>
                        </div>
                      </div>

                      <div style={{ background: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: 8, padding: 12, marginBottom: 12 }}>
                        <div style={{ fontSize: 11, fontWeight: 700, color: '#6b7280', marginBottom: 6 }}>FEATURES INCLUDED</div>
                        <div style={{ fontSize: 12, color: '#374151' }}>
                          {Array.from(selected).slice(0, 6).join(', ')}{selected.size > 6 ? ` +${selected.size - 6} more` : ''}
                        </div>
                      </div>

                      <div style={{ background: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: 8, padding: 12, marginBottom: 4 }}>
                        <div style={{ fontSize: 11, fontWeight: 700, color: '#6b7280', marginBottom: 6 }}>LAUNCH URL</div>
                        <div style={{ fontSize: 11, color: '#374151', fontFamily: 'monospace', wordBreak: 'break-all', maxHeight: 48, overflow: 'hidden' }}>
                          {setupUrl.substring(0, 120)}…
                        </div>
                      </div>
                      <div style={{ fontSize: 11, color: '#9ca3af', marginTop: 4 }}>The wizard will open in the admin panel with all features &amp; permissions pre-loaded.</div>
                    </>
                  )}

                  <div style={{ marginTop: 12, background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: 8, padding: 12, fontSize: 12, color: '#1e40af' }}>
                    ℹ️ Clicking <strong>"🚀 Launch Onboarding Wizard"</strong> will navigate directly to the <em>Client Onboarding Wizard</em> tab with all selected features and derived permissions pre-loaded. Use <strong>"Copy URL"</strong> if you want to share the setup link externally.
                  </div>
                </div>
              )}
            </div>

            {/* Footer */}
            <div style={{ padding: '14px 24px', borderTop: '1px solid #e5e7eb', display: 'flex', gap: 8, justifyContent: 'flex-end', flexShrink: 0, flexWrap: 'wrap' }}>
              <button onClick={() => setModalOpen(false)} style={{ padding: '8px 20px', borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: 'pointer', background: '#f3f4f6', color: '#111827', border: '1px solid #e5e7eb' }}>Cancel</button>
              {modalTab === 'spec' && (
                <>
                  <button onClick={downloadSpec} style={{ padding: '8px 20px', borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: 'pointer', background: '#0a74da', color: '#fff', border: 'none' }}>⬇ Download .html</button>
                  <button onClick={copySpecText} style={{ padding: '8px 20px', borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: 'pointer', background: '#059669', color: '#fff', border: 'none' }}>📋 Copy Text</button>
                  <button onClick={copyJSON} style={{ padding: '8px 20px', borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: 'pointer', background: '#6b7280', color: '#fff', border: 'none' }}>{'{ } JSON'}</button>
                </>
              )}
              {modalTab === 'setup' && (
                <>
                  <button onClick={copySetupURL} style={{ padding: '8px 20px', borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: 'pointer', background: '#6b7280', color: '#fff', border: 'none' }}>📋 Copy URL</button>
                  <button onClick={launchAdminSetup} style={{ padding: '8px 20px', borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: 'pointer', background: '#7c3aed', color: '#fff', border: 'none' }}>🚀 Launch Onboarding Wizard</button>
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {/* ── TOAST ── */}
      <div style={{
        position: 'fixed', bottom: 24, left: '50%',
        transform: `translateX(-50%) translateY(${toast.visible ? '0' : '80px'})`,
        background: '#1e1e2d', color: '#fff', padding: '10px 20px', borderRadius: 30,
        fontSize: 13, fontWeight: 600, zIndex: 9999, transition: 'transform .3s',
        whiteSpace: 'nowrap', pointerEvents: 'none',
      }}>
        {toast.msg}
      </div>
    </div>
  );
};

/* ═══════════════════════════════════════════════════
   BADGE STYLES
═══════════════════════════════════════════════════ */
function badgeStyle(type: string): React.CSSProperties {
  const base: React.CSSProperties = { display: 'inline-flex', alignItems: 'center', gap: 3, padding: '2px 8px', borderRadius: 20, fontSize: 10, fontWeight: 700, letterSpacing: '.2px' };
  const map: Record<string, React.CSSProperties> = {
    'b-live':    { ...base, background: '#d1fae5', color: '#065f46' },
    'b-partial': { ...base, background: '#e0e7ff', color: '#3730a3' },
    'b-mock':    { ...base, background: '#fef3c7', color: '#92400e' },
    'b-wip':     { ...base, background: '#fee2e2', color: '#991b1b' },
    'b-todo':    { ...base, background: '#f3e8ff', color: '#6b21a8' },
    'b-admin':   { ...base, background: '#ede9fe', color: '#5b21b6' },
    'b-dep':     { ...base, background: '#f3f4f6', color: '#374151', border: '1px solid #e5e7eb' },
  };
  return map[type] || base;
}

export default ClientFeatureSelector;
